import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useField } from '@unform/core'
import { Search } from '@vtex/shoreline'
import * as Yup from 'yup'

import * as S from './styles'

/**
 *
 * @example
 *  <InputSearch
 *    name="address_street"
 *    placeholder="Insira a rua"
 *    loading={loadingCep}
 *    icon={FiSearch}
 *  />
 *
 * @param {string} name Recebe o name para o input
 * @param {ReactElement | string} icon Ícone que será exbido a esquerda do input.
 * @param {boolean} loading Exibe um loader ao lado do input
 * @param {function} onBlur Extendendo onBlur
 * @param {string} helpText Exibe texto explicativo sobre o campo
 *
 */

const InputSearch: React.FC<InputProps> = ({
  name,
  loading,
  onBlur,
  onChange,
  disabled = false,
  helpText,
  ...rest
}) => {
  const inputRef = useRef(null)

  const [isFilled, setIsFilled] = useState(false)
  const [valueInput, setValueInput] = useState('')

  const { fieldName, defaultValue, error, registerField, clearError } =
    useField(name)

  const handleInputBlur = useCallback(() => {
    let isFilled = !!inputRef.current?.value

    if (rest.type === 'email') {
      const schema = Yup.string().email().required()

      isFilled = schema.isValidSync(inputRef.current?.value)
    }

    setIsFilled(isFilled)

    if (onBlur) {
      onBlur({
        value: inputRef.current.value,
        name: inputRef.current.name,
        clearError,
        setIsFilled
      })
    }

    if (inputRef.current?.value) {
      clearError()
    }
  }, [clearError, onBlur])

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value'
    })
  }, [fieldName, registerField])

  const defaultValueIsNull = defaultValue === null

  const defaultValueIsUndefined = typeof defaultValue === 'undefined'

  useEffect(() => {
    if (!defaultValueIsNull && !defaultValueIsUndefined) {
      setValueInput(defaultValue)
      setIsFilled(true)
    }
  }, [defaultValue, defaultValueIsNull, defaultValueIsUndefined])

  const inputChangedHandler = e => {
    const { value, name } = e.target

    onChange && onChange({ value, name })

    setValueInput(value)
  }

  return (
    <>
      <div style={{ zIndex: 7, position: 'relative' }}>
        <Search
          name={fieldName}
          autocomplete="off"
          onBlur={handleInputBlur}
          type="text"
          defaultValue={defaultValue}
          id={name}
          onChange={inputChangedHandler}
          disabled={disabled}
          loading={loading}
          {...rest}
          ref={inputRef}
        />
      </div>

      {helpText && <S.MessageHelp>{helpText}</S.MessageHelp>}
      {error && <S.MessageError>{error}</S.MessageError>}
    </>
  )
}

export default InputSearch
